'use client'
import { useAccount, useBalance, useChainId, useDisconnect, useSignMessage } from 'wagmi'

import EditProfileDialog from '@/components/global/EditProfileDialog'
import { Button, ButtonProps } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { getNonce } from '@/lib/api/auth/get-nonce'
import { getUserByAddress } from '@/lib/api/user/get-user-by-address'
import { getChainConfig } from '@/lib/chains'
import { FLARE_PUMP_CONTRACT } from '@/lib/hooks/useErc20Contract'
import { _log, cn, isJwtExpired } from '@/lib/utils'
import { trimAddress } from '@/lib/web3/utils'
import { useConnectModal } from '@rainbow-me/rainbowkit'
import { ChevronDown, UserIcon } from 'lucide-react'
import { useAction } from 'next-safe-action/hooks'
import Image from 'next/image'
import Link from 'next/link'
import { useDeferredValue, useEffect, useState } from 'react'
import { toast } from 'sonner'
import { formatUnits } from 'viem'
import { IpfsImage } from '../../../ui/ipfs-image'
import { NetworkSelector } from './NetworkSelector'
import { useUserProvider } from '@/lib/context/user-provider'

type Props = ButtonProps & {
  prices: Record<string, number>
  className?: string
}
export default function ConnectWallet({ prices, className }: Props) {
  const { openConnectModal } = useConnectModal()
  const { disconnect } = useDisconnect()
  const { signMessageAsync } = useSignMessage()
  const chainId = useChainId()
  const [isOpen, setIsOpen] = useState(false)
  const { address } = useAccount()
  const deferredAddress = useDeferredValue(address)

  const { user, isLoggedIn, login, fetchUser, updateProfile, follow, unfollow, followToken, unfollowToken } =
    useUserProvider() || {}

  useEffect(() => {
    if (!address || !fetchUser) return
    fetchUser()
  }, [address, fetchUser])

  function handleDisconnect() {
    disconnect()
    localStorage.removeItem('volatile-auth')
    setIsOpen(false)
  }

  function handleClick() {
    if (login) {
      login()
    } else if (openConnectModal) {
      openConnectModal()
    }
    setIsOpen(true)
  }

  const { address: userAddress } = useAccount()
  const { data } = useBalance({ address: userAddress })
  const displayBalance = Number(formatUnits(BigInt(data?.value ?? 0), data?.decimals ?? 18))?.toFixed(2)

  useEffect(() => {
    // User provider handles auto-login, this is just for fetching user data
    if (!address || !fetchUser) return
    fetchUser()
  }, [address, fetchUser])

  if (!address || !isLoggedIn)
    return (
      <Button
        variant={'outline'}
        className={cn('h-10', className)}
        onClick={handleClick}
      >
        <p className={' text-sm'}>Connect wallet</p>
      </Button>
    )

  const menuLinks = [
    {
      label: 'Go to profile',
      href: `/user/${address}`,
      icon: <UserIcon width={16} height={16} />,
      onClick: () => setIsOpen(false),
      className: 'hover:bg-primary md:pl-4 py-2 flex items-center gap-2',
      type: 'link',
    },
    {
      label: 'Disconnect wallet',
      icon: (
        <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="none" viewBox="0 0 24 24" stroke="currentColor" className="inline-block text-error"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 11V7a4 4 0 118 0v4m-8 0h8m-8 0a2 2 0 00-2 2v6a2 2 0 002 2h8a2 2 0 002-2v-6a2 2 0 00-2-2m-8 0h8" /></svg>
      ),
      onClick: handleDisconnect,
      className: 'hover:bg-negative/20 text-negative flex items-center gap-2 md:pl-4 py-2 text-left font-semibold',
      type: 'button',
    },
  ]

  const content = (
    <>
      <div className="w-full bg-card px-0 py-4 md:px-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center justify-start gap-2.5">
            <Image
              className="rounded-full object-cover"
              src={user?.avatarUrl ? `${process.env.NEXT_PUBLIC_API}/public${user!.avatarUrl}` : ''}
              alt="alt"
              width={32}
              height={32}
            />
            <div>
              <p className="font-roboto-mono font-medium text-sm">{trimAddress(address)}</p>
              <p className="text-primary text-xs">@{!user?.name ? 'User' : user.name}</p>
            </div>
          </div>
          <EditProfileDialog>
            <button className="text-xs font-medium">Edit</button>
          </EditProfileDialog>
        </div>
      </div>
      <div className="px-0 md:px-3 py-3">
        <p className="text-tertiary text-xs font-medium pb-2">Your asset</p>
        <div className="flex items-center justify-between">
          <div className="inline-flex items-center gap-1">
            <Image
              src={getChainConfig(chainId).iconUrl}
              alt={`${getChainConfig(chainId).name} logo`}
              width={22}
              height={22}
              className="size-5 rounded-full object-cover"
            />
            <p className="text-white font-medium text-xs">{getChainConfig(chainId).nativeSymbol}</p>
          </div>
          <div className="inline-flex items-center gap-2">
            <p className="font-medium text-xs">
              {displayBalance}&nbsp;<span className="text-tertiary">{getChainConfig(chainId).nativeSymbol}</span>
            </p>
            <p className="font-medium text-xs">
              <span className="text-primary">$</span>
              {(Number(displayBalance) * prices[chainId.toString()]).toFixed(2)}
            </p>
          </div>
        </div>
      </div>
      <div className="border-t flex flex-col justify-center w-full text-xs font-medium">
        {menuLinks.map(link =>
          link.type === 'link' ? (
            <Link
              key={link.label}
              href={link.href || '/'}
              onClick={link.onClick}
              className={link.className}
            >
              {link.icon}
              {link.label}
            </Link>
          ) : (
            <button
              key={link.label}
              className={link.className}
              onClick={link.onClick}
            >
              {link.icon}
              {link.label}
            </button>
          )
        )}
      </div>
    </>
  )

  return (
    <>
      <div className="flex md:hidden flex-col mb-4">{content}</div>
      <div className="gap-2 hidden md:flex center">
        <NetworkSelector />
        <DropdownMenu
          open={isOpen}
          onOpenChange={setIsOpen}
        >
          <DropdownMenuTrigger asChild>
            <Button
              variant={'outline'}
              className={'h-10 pl-3 bg-card'}
              onClick={handleClick}
            >
              <Image
                className={'rounded-full object-cover size-5'}
                src={user?.avatarUrl ? `${process.env.NEXT_PUBLIC_API}/public${user!.avatarUrl}` : ''}
                alt={'alt'}
                width={22}
                height={22}
              />
              <p className={' text-sm'}>{trimAddress(address)}</p>
              <ChevronDown />
            </Button>
          </DropdownMenuTrigger>
          <DropdownMenuContent className={'w-72 rounded-lg border bg-background'}>{content}</DropdownMenuContent>
        </DropdownMenu>
      </div>
    </>
  )
}
